import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { Box, Checkbox, Chip, Collapse, FormControlLabel, IconButton, Tooltip, Typography } from "@mui/material";
import { ExpandMore, ExpandLess, CheckCircle, Lock, Cancel, Info as InfoIcon } from "@mui/icons-material";

import { ActionButton, CardLayout, InputField, SelectField, ToggleButton } from "../v3";
import { CompanyService, SubscriptionService } from "../../services";
import { buildLimitsPayload, LimitValue, limitChipLabel, MODULE_LIMIT_KEYS } from "../../pages/superadmin/planLimits.util";
import { planValidationRules } from "../../pages/superadmin/validation";

const BILLING_CYCLE = [
  { id: 1, name: "Monthly", value: "MONTHLY" },
  { id: 2, name: "Yearly", value: "YEARLY" },
];

interface ModuleLimits {
  [key: string]: LimitValue;
}

interface ModuleState {
  active: boolean;
  limits: ModuleLimits;
  includedFeatureCodes: string[];
  hiddenFeatureCodes: string[];
}

interface FeatureItem {
  id: number;
  code: string;
  name: string;
  type: string;
  children?: FeatureItem[];
}

export const AddEditPlanForm = ({
  companyId = null,
  planId,
}: {
  companyId?: string | number | null;
  planId?: string | number;
}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [modules, setModules] = useState<FeatureItem[]>([]);
  const [moduleStates, setModuleStates] = useState<Record<string, ModuleState>>({});
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});
  const [planFeaturesFromAPI, setPlanFeaturesFromAPI] = useState<any[]>([]);
  const [selectedBilling, setSelectedBilling] = useState(null);
  const [originalDates, setOriginalDates] = useState({
    startDate: null,
    endDate: null,
  });
  const originalPlanDetailsRef = useRef<any>(null);
  const originalModuleStatesRef = useRef<Record<string, ModuleState> | null>(null);
  const [planDetails, setPlanDetails] = useState({
    companyId: Number(companyId),
    name: undefined,
    type: "CUSTOM",
    billingCycle: undefined,
    price: undefined,
    description: undefined,
    startDate: undefined,
    endDate: undefined,
    webAccess: true,
    mobileAccess: true,
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (!planId && companyId) fetchData(companyId);
    if (planId) fetchPlan(planId);
  }, [companyId, planId]);

  useEffect(() => {
    fetchModules();
  }, []);

  useEffect(() => {
    if (modules.length === 0 || planFeaturesFromAPI.length === 0) return;

    const states: Record<string, ModuleState> = {};
    modules.forEach((mod) => {
      const pf = planFeaturesFromAPI.find((pf) => pf.featureId === mod.id);
      states[mod.code] = {
        active: pf ? pf.active : false,
        limits: {},
        includedFeatureCodes: pf?.limits?.includedFeatureCodes || [],
        hiddenFeatureCodes: pf?.limits?.hiddenFeatureCodes || [],
      };

      if (pf?.limits) {
        const limitDefs = MODULE_LIMIT_KEYS[mod.code] || [];
        limitDefs.forEach(({ key }) => {
          if (pf.limits[key] !== undefined) {
            states[mod.code].limits[key] = pf.limits[key];
          }
        });
      }
    });

    setModuleStates(states);
    if (planId) {
      originalModuleStatesRef.current = JSON.parse(JSON.stringify(states));
    }
  }, [modules, planFeaturesFromAPI, planId]);

  const fetchData = async (id: string | number) => {
    try {
      const { data: company } = await CompanyService.getCompanyDetails(id);
      setPlanDetails((prev) => ({ ...prev, name: company.name }));
    } catch (error) {
      console.error("Error fetching company:", error);
    }
  };

  const fetchPlan = async (id: string | number) => {
    try {
      const { data: plan } = await SubscriptionService.getPlanById(id);
      const formattedStart = moment(plan.startDate).format("YYYY-MM-DD");
      const formattedEnd = moment(plan.endDate).format("YYYY-MM-DD");

      setPlanDetails((prev) => ({
        ...prev,
        name: plan.name,
        type: plan.type,
        billingCycle: plan.billingCycle,
        price: plan.price,
        description: plan.description,
        startDate: formattedStart,
        endDate: formattedEnd,
        webAccess: plan.webAccess ?? true,
        mobileAccess: plan.mobileAccess ?? true,
      }));

      setOriginalDates({
        startDate: formattedStart,
        endDate: formattedEnd,
      });

      originalPlanDetailsRef.current = {
        name: plan.name,
        billingCycle: plan.billingCycle,
        price: plan.price,
        description: plan.description,
        webAccess: plan.webAccess ?? true,
        mobileAccess: plan.mobileAccess ?? true,
      };

      setSelectedBilling(BILLING_CYCLE.find((item) => item.value === plan.billingCycle));
      setPlanFeaturesFromAPI(plan.planFeatures || []);
    } catch (error) {
      console.error("Error fetching plan:", error);
    }
  };

  const fetchModules = async () => {
    try {
      const { data } = await SubscriptionService.getAllModules();
      setModules(data);

      const initialStates: Record<string, ModuleState> = {};
      data.forEach((mod) => {
        initialStates[mod.code] = {
          active: false,
          limits: {},
          includedFeatureCodes: (mod.children || []).map((c) => c.code),
          hiddenFeatureCodes: [],
        };
      });
      setModuleStates(initialStates);
    } catch (error) {
      console.error("Error fetching modules:", error);
    }
  };

  const updateDetailsState = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setPlanDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleBillingChange = (selected: any) => {
    setSelectedBilling(selected);
    setPlanDetails((prev) => ({ ...prev, billingCycle: selected.value }));
  };

  const getBillingCycleOptions = () =>
    Promise.resolve({
      data: BILLING_CYCLE,
    } as any);

  const handleCancelClick = () => {
    setPlanDetails({
      companyId: Number(companyId),
      name: undefined,
      type: "CUSTOM",
      billingCycle: undefined,
      price: undefined,
      description: undefined,
      startDate: undefined,
      endDate: undefined,
      webAccess: true,
      mobileAccess: true,
    });
    setSelectedBilling(null);
    navigate(-1);
  };

  const handleModuleActiveChange = (moduleCode: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setModuleStates((prev) => ({
      ...prev,
      [moduleCode]: {
        ...prev[moduleCode],
        active: event.target.checked,
      },
    }));
  };

  const handleLimitChange = (moduleCode: string, limitKey: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setModuleStates((prev) => ({
      ...prev,
      [moduleCode]: {
        ...prev[moduleCode],
        limits: {
          ...prev[moduleCode].limits,
          [limitKey]: value === "" ? "" : Number(value),
        },
      },
    }));
  };

  const handleSelectLimitChange = (moduleCode: string, limitKey: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    setModuleStates((prev) => ({
      ...prev,
      [moduleCode]: {
        ...prev[moduleCode],
        limits: {
          ...prev[moduleCode].limits,
          [limitKey]: value,
        },
      },
    }));
  };

  const handleBooleanLimitChange = (moduleCode: string, limitKey: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setModuleStates((prev) => ({
      ...prev,
      [moduleCode]: {
        ...prev[moduleCode],
        limits: {
          ...prev[moduleCode].limits,
          [limitKey]: checked,
        },
      },
    }));
  };

  const handleFeatureToggle = (moduleCode: string, featureCode: string) => {
    setModuleStates((prev) => {
      const state = prev[moduleCode];
      if (!state) return prev;

      const isIncluded = state.includedFeatureCodes.includes(featureCode);
      const isHidden = state.hiddenFeatureCodes.includes(featureCode);

      let newIncluded = [...state.includedFeatureCodes];
      let newHidden = [...state.hiddenFeatureCodes];

      if (isIncluded) {
        newIncluded = newIncluded.filter((c) => c !== featureCode);
        newHidden.push(featureCode);
      } else if (isHidden) {
        newHidden = newHidden.filter((c) => c !== featureCode);
      } else {
        newIncluded.push(featureCode);
      }

      return {
        ...prev,
        [moduleCode]: {
          ...state,
          includedFeatureCodes: newIncluded,
          hiddenFeatureCodes: newHidden,
        },
      };
    });
  };

  const handleIncludeAll = (moduleCode: string, allFeatureCodes: string[]) => {
    setModuleStates((prev) => ({
      ...prev,
      [moduleCode]: {
        ...prev[moduleCode],
        includedFeatureCodes: allFeatureCodes,
        hiddenFeatureCodes: [],
      },
    }));
  };

  const handleExcludeAll = (moduleCode: string) => {
    setModuleStates((prev) => ({
      ...prev,
      [moduleCode]: {
        ...prev[moduleCode],
        includedFeatureCodes: [],
        hiddenFeatureCodes: [],
      },
    }));
  };

  const toggleModuleExpand = (moduleCode: string) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleCode]: !prev[moduleCode],
    }));
  };

  const allChecked = modules.length > 0 && modules.every((mod) => moduleStates[mod.code]?.active);
  const someChecked = modules.some((mod) => moduleStates[mod.code]?.active) && !allChecked;

  const handleParentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    const updated: Record<string, ModuleState> = {};
    modules.forEach((mod) => {
      updated[mod.code] = {
        ...moduleStates[mod.code],
        active: checked,
      };
    });
    setModuleStates(updated);
  };

  const buildPlanFeatures = () => {
    return Object.entries(moduleStates).map(([moduleCode, state]) => {
      const mod = modules.find((m) => m.code === moduleCode);
      const limitDefs = MODULE_LIMIT_KEYS[moduleCode] || [];

      const limits = buildLimitsPayload(state, limitDefs);

      return {
        featureId: mod.id,
        active: state.active,
        ...(Object.keys(limits).length > 0 ? { limits } : {}),
      };
    });
  };

  const handleSavePlan = async () => {
    if (loading) return;
    setErrors({});
    const planFeatures = buildPlanFeatures();
    const payloadForValidation = {
      ...planDetails,
      planFeatures,
    };
    const validationErrors = planValidationRules(payloadForValidation);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);

      const payload: any = {
        ...planDetails,
        price: Number(planDetails.price),
        planFeatures,
      };

      if (planId) {
        delete payload.startDate;
        delete payload.endDate;
        delete payload.type;
        delete payload.companyId;

        const original = originalPlanDetailsRef.current;
        const otherFieldsChanged =
          !original ||
          planDetails.name !== original.name ||
          planDetails.billingCycle !== original.billingCycle ||
          Number(planDetails.price) !== Number(original.price) ||
          planDetails.description !== original.description ||
          planDetails.webAccess !== original.webAccess ||
          planDetails.mobileAccess !== original.mobileAccess ||
          JSON.stringify(moduleStates) !== JSON.stringify(originalModuleStatesRef.current);

        if (otherFieldsChanged) {
          await SubscriptionService.updatePlan(planId, payload);
        }

        const startChanged = planDetails.startDate !== originalDates.startDate;
        const endChanged = planDetails.endDate !== originalDates.endDate;

        if (startChanged || endChanged) {
          await SubscriptionService.updatePlanValidity(planId, {
            price: Number(planDetails.price),
            startDate: moment(planDetails.startDate).startOf("day").utc().toISOString(),
            endDate: moment(planDetails.endDate).endOf("day").utc().toISOString(),
          });
        }
      } else {
        await SubscriptionService.createPlan({
          ...payload,
          startDate: moment(planDetails.startDate).startOf("day").utc().toISOString(),
          endDate: moment(planDetails.endDate).endOf("day").utc().toISOString(),
        });
      }

      toast.success(`${planId ? "Updated" : "Created"} plan successfully`);
      navigate(-1);
    } catch (error) {
      toast.error(`Failed to ${planId ? "update" : "create"} plan`);
      console.error("Error saving plan:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CardLayout>
      <Box display="flex" gap={2}>
        <InputField
          required
          label="Name"
          name="name"
          value={planDetails.name}
          onChange={updateDetailsState}
          error={!!errors.name}
          helperText={errors.name}
          fullWidth
        />
        <SelectField
          label="Billing"
          value={selectedBilling}
          getData={getBillingCycleOptions}
          required
          error={!!errors.billingCycle}
          helperText={errors.billingCycle}
          onChange={handleBillingChange as any}
          style={{ width: "100%" }}
        />
        <InputField
          required
          type="number"
          label="Price"
          name="price"
          value={planDetails.price}
          onChange={updateDetailsState}
          error={!!errors.price}
          helperText={errors.price}
          fullWidth
        />
      </Box>

      <Box display="flex" gap={2}>
        <InputField
          label="Description"
          name="description"
          value={planDetails.description}
          onChange={updateDetailsState}
          error={!!errors.description}
          helperText={errors.description}
          fullWidth
        />
        <InputField
          required
          type="date"
          label="Start Date"
          name="startDate"
          value={planDetails?.startDate}
          onChange={updateDetailsState}
          error={!!errors.startDate}
          helperText={errors.startDate}
          fullWidth
        />
        <InputField
          required
          type="date"
          label="End Date"
          name="endDate"
          value={planDetails?.endDate}
          onChange={updateDetailsState}
          error={!!errors.endDate}
          helperText={errors.endDate}
          fullWidth
        />
      </Box>

      <Box display="flex" alignItems="center" gap={3}>
        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>
          Platform Access
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <ToggleButton
            label="Web"
            checked={!!planDetails.webAccess}
            onToggleChange={(e) => setPlanDetails((prev) => ({ ...prev, webAccess: e.target.checked }))}
          />
          <ToggleButton
            label="Mobile"
            checked={!!planDetails.mobileAccess}
            onToggleChange={(e) => setPlanDetails((prev) => ({ ...prev, mobileAccess: e.target.checked }))}
          />
        </Box>
        {errors.platformAccess && (
          <Typography color="error" variant="caption">
            {errors.platformAccess}
          </Typography>
        )}
      </Box>

      <Box>
        <FormControlLabel
          label="Modules"
          control={
            <Checkbox
              checked={allChecked}
              indeterminate={someChecked}
              onChange={handleParentChange}
              sx={{
                "&.Mui-checked": { color: "#674BF5" },
                "&.MuiCheckbox-indeterminate": { color: "#674BF5" },
              }}
            />
          }
        />

        <Box sx={{ display: "flex", flexWrap: "wrap", ml: 3, gap: 1.5 }}>
          {modules.map((mod) => {
            const state = moduleStates[mod.code] || {
              active: false,
              limits: {},
              includedFeatureCodes: (mod.children || []).map((c) => c.code),
              hiddenFeatureCodes: [],
            };
            const limitDefs = MODULE_LIMIT_KEYS[mod.code] || [];
            const hasLimitDefs = limitDefs.length > 0;
            const hasFeatures = (mod.children || []).length > 0;
            const isExpanded = expandedModules[mod.code] || false;

            return (
              <Box
                key={mod.code}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  overflow: "hidden",
                  flex: "1 1 calc(50% - 12px)",
                  minWidth: 280,
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 1.5, py: 1, backgroundColor: state.active ? "#F3EFFD" : "transparent" }}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Checkbox
                      checked={!!state.active}
                      onChange={handleModuleActiveChange(mod.code)}
                      sx={{ "&.Mui-checked": { color: "#674BF5" } }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      {mod.name}
                    </Typography>
                    {(hasFeatures || hasLimitDefs) && state.active && (
                      <Box display="flex" gap={0.5} ml={1}>
                        {limitDefs.map((def) => {
                          const chipLabel = limitChipLabel(def, state.limits[def.key] ?? def.defaultValue);
                          return chipLabel ? (
                            <Chip
                              key={def.key}
                              label={chipLabel}
                              size="small"
                              sx={{ backgroundColor: "#EEE8FF", color: "#674BF5", fontWeight: 500, fontSize: 11, height: 20 }}
                            />
                          ) : null;
                        })}
                        {hasFeatures &&
                          (mod.children || []).length - state.includedFeatureCodes.length - state.hiddenFeatureCodes.length >
                            0 && (
                            <Chip
                              label={`${(mod.children || []).length - state.includedFeatureCodes.length - state.hiddenFeatureCodes.length}/${(mod.children || []).length} excluded`}
                              size="small"
                              sx={{ backgroundColor: "#FDECEA", color: "#B71C1C", fontWeight: 500, fontSize: 11, height: 20 }}
                            />
                          )}
                        {state.hiddenFeatureCodes.length > 0 && (
                          <Chip
                            label={`${state.hiddenFeatureCodes.length}/${(mod.children || []).length} locked`}
                            size="small"
                            sx={{ backgroundColor: "#E8F5E9", color: "#2E7D32", fontWeight: 500, fontSize: 11, height: 20 }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                  <Box display="flex" alignItems="center">
                    {(hasLimitDefs || hasFeatures) && state.active && (
                      <IconButton size="small" onClick={() => toggleModuleExpand(mod.code)}>
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    )}
                  </Box>
                </Box>

                <Collapse in={isExpanded && state.active} timeout="auto" unmountOnExit>
                  <Box sx={{ px: 2, pb: 2, pt: 1 }}>
                    {hasLimitDefs && (
                      <Box mb={2}>
                        <Typography variant="caption" fontWeight={600} sx={{ color: "#674BF5", mb: 1, display: "block" }}>
                          Resource Limits
                        </Typography>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          {limitDefs.map(({ key, label, type, options, defaultValue }) =>
                            type === "select" ? (
                              <InputField
                                key={key}
                                select
                                label={label}
                                name={key}
                                value={String(state.limits[key] ?? defaultValue ?? "")}
                                selectOptions={(options || []).map(({ label, value }) => ({ label, value: String(value) }))}
                                onChange={handleSelectLimitChange(mod.code, key)}
                                style={{ width: 200 }}
                              />
                            ) : type === "boolean" ? (
                              <FormControlLabel
                                key={key}
                                label={label}
                                control={
                                  <Checkbox
                                    checked={state.limits[key] === true}
                                    onChange={handleBooleanLimitChange(mod.code, key)}
                                    sx={{ "&.Mui-checked": { color: "#674BF5" } }}
                                  />
                                }
                              />
                            ) : (
                              <InputField
                                key={key}
                                label={label}
                                type="number"
                                name={key}
                                value={state.limits[key] ?? ""}
                                onChange={handleLimitChange(mod.code, key)}
                                style={{ width: 200 }}
                              />
                            ),
                          )}
                        </Box>
                      </Box>
                    )}

                    {hasFeatures && (
                      <Box>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <CheckCircle sx={{ fontSize: 14, color: "#4CAF50" }} />
                              <Typography variant="caption" color="text.secondary">
                                Included
                              </Typography>
                              <Tooltip title="Included features are available in the plan. Users with the right role can access these features.">
                                <InfoIcon fontSize="small" sx={{ color: "#9e9e9e", fontSize: 13, cursor: "help" }} />
                              </Tooltip>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Lock sx={{ fontSize: 14, color: "#2E7D32" }} />
                              <Typography variant="caption" color="text.secondary">
                                Always-On
                              </Typography>
                              <Tooltip title="Always-On features are always enabled for all users and cannot be toggled in role assignments. Useful for enforcing features like back date entry in lower-tier plans.">
                                <InfoIcon fontSize="small" sx={{ color: "#9e9e9e", fontSize: 13, cursor: "help" }} />
                              </Tooltip>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Cancel sx={{ fontSize: 14, color: "#B71C1C" }} />
                              <Typography variant="caption" color="text.secondary">
                                Excluded
                              </Typography>
                              <Tooltip title="Excluded features are removed from the plan. Users in this company cannot access these features at all.">
                                <InfoIcon fontSize="small" sx={{ color: "#9e9e9e", fontSize: 13, cursor: "help" }} />
                              </Tooltip>
                            </Box>
                          </Box>
                          <Box display="flex" gap={1}>
                            <Chip
                              label="Include All"
                              size="small"
                              clickable
                              onClick={() =>
                                handleIncludeAll(
                                  mod.code,
                                  (mod.children || []).map((c) => c.code),
                                )
                              }
                              sx={{
                                backgroundColor: "#E8F5E9",
                                color: "#2E7D32",
                                fontWeight: 500,
                                fontSize: 11,
                                height: 22,
                                cursor: "pointer",
                              }}
                            />
                            <Chip
                              label="Exclude All"
                              size="small"
                              clickable
                              onClick={() => handleExcludeAll(mod.code)}
                              sx={{
                                backgroundColor: "#FDECEA",
                                color: "#B71C1C",
                                fontWeight: 500,
                                fontSize: 11,
                                height: 22,
                                cursor: "pointer",
                              }}
                            />
                          </Box>
                        </Box>
                        <Box display="flex" flexWrap="wrap" gap={0.75}>
                          {(mod.children || []).map((feature) => {
                            const isIncluded = state.includedFeatureCodes.includes(feature.code);
                            const isHidden = state.hiddenFeatureCodes.includes(feature.code);
                            const isExcluded = !isIncluded && !isHidden;
                            return (
                              <Box
                                key={feature.code}
                                onClick={() => handleFeatureToggle(mod.code, feature.code)}
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  cursor: "pointer",
                                  userSelect: "none",
                                  backgroundColor: isExcluded ? "#FDECEA" : "#E8F5E9",
                                  border: `1px solid ${isExcluded ? "#FFCDD2" : "#C8E6C9"}`,
                                  "&:hover": { opacity: 0.8 },
                                }}
                              >
                                {isExcluded ? (
                                  <Cancel sx={{ fontSize: 14, color: "#B71C1C" }} />
                                ) : isHidden ? (
                                  <Lock sx={{ fontSize: 14, color: "#2E7D32" }} />
                                ) : (
                                  <CheckCircle sx={{ fontSize: 14, color: "#4CAF50" }} />
                                )}
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: isExcluded ? "#B71C1C" : isHidden ? "#2E7D32" : "text.primary",
                                    textDecoration: isExcluded ? "line-through" : "none",
                                    fontSize: 12,
                                  }}
                                >
                                  {feature.name}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box className={"d-flex justify-content-end gap-1"} sx={{ pt: 3 }}>
        <ActionButton buttonText="cancel" variant="outlined" onButtonClick={handleCancelClick} />
        {/* @ts-ignore */}
        <ActionButton buttonText="Submit" color="primary" onButtonClick={handleSavePlan as any} loading={loading} />
      </Box>
    </CardLayout>
  );
};
